/**
 * Translation Context and Hook
 *
 * Provides a React Context-based translation system that integrates with Rails i18n.
 * Translations are passed from Rails views and made available throughout the React component tree.
 *
 * Usage:
 * 1. Wrap your app with TranslationProvider (done in providers.tsx)
 * 2. Use the useTranslation() hook in any component to access translations
 *
 * @example
 * // In a component (keys are relative to the translations object passed to the provider):
 * const { t } = useTranslation();
 * return <button>{t('create.buttonLabel')}</button>;
 *
 * @example
 * // With fallback:
 * const { t } = useTranslation();
 * return <span>{t('form.labels.name', 'Name')}</span>;
 */

import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';

// Deep nested translation object type
export type TranslationValue = string | TranslationObject;
export interface TranslationObject {
  [key: string]: TranslationValue;
}

// Context type
interface TranslationContextValue {
  translations: TranslationObject;
  /**
   * Get a translation by dot-notation key
   * @param key - Dot-notation key (e.g., 'users.form.labels.name')
   * @param fallbackOrParams - Fallback string, or interpolation params (e.g., { name: 'Joy' })
   * @returns The translation string with interpolations applied
   */
  t: (key: string, fallbackOrParams?: string | Record<string, string | number>) => string;
  /**
   * Get a nested translation object
   * @param key - Dot-notation key to a nested object
   * @returns The translation object or undefined
   */
  getSection: <T extends TranslationObject>(key: string) => T | undefined;
}

// Create context with default values
const TranslationContext = createContext<TranslationContextValue>({
  translations: {},
  t: (key: string, fallback?: string) => fallback || key,
  getSection: () => undefined,
});

interface TranslationProviderProps {
  translations: TranslationObject;
  children: ReactNode;
}

/**
 * Navigate to a nested value using dot notation
 */
function getNestedValue(obj: TranslationObject, path: string): TranslationValue | undefined {
  const keys = path.split('.');
  let current: TranslationValue | undefined = obj;

  for (const key of keys) {
    if (current === undefined || typeof current === 'string') {
      return undefined;
    }
    current = current[key];
  }

  return current;
}

/**
 * TranslationProvider - Provides translations to all child components
 *
 * @example
 * // In your root component:
 * <TranslationProvider translations={translationsFromRails}>
 *   <App />
 * </TranslationProvider>
 */
export function TranslationProvider({ translations, children }: TranslationProviderProps) {
  const t = useCallback((key: string, fallbackOrParams?: string | Record<string, string | number>): string => {
    const value = getNestedValue(translations, key);
    const params = typeof fallbackOrParams === 'object' ? fallbackOrParams : undefined;
    const fallback = typeof fallbackOrParams === 'string' ? fallbackOrParams : undefined;

    const resolved = typeof value === 'string' ? value : (fallback ?? key);

    if (params) {
      // Translations come from Rails i18n, so support its %{name} token form
      // alongside the original {{name}} form.
      return resolved
        .replace(/\{\{(\w+)\}\}/g, (_, name) => (params[name] != null ? String(params[name]) : ''))
        .replace(/%\{(\w+)\}/g, (_, name) => (params[name] != null ? String(params[name]) : ''));
    }

    return resolved;
  }, [translations]);

  const getSection = useCallback(<T extends TranslationObject>(key: string): T | undefined => {
    const value = getNestedValue(translations, key);

    if (typeof value === 'object' && value !== null) {
      return value as T;
    }

    return undefined;
  }, [translations]);

  const contextValue = useMemo(() => ({
    translations,
    t,
    getSection,
  }), [translations, t, getSection]);

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * useTranslation hook - Access translations from any component
 *
 * @returns Object containing:
 *   - t: Function to get a translation by key
 *   - getSection: Function to get a nested translation object
 *   - translations: The full translations object
 *
 * @example
 * const { t, getSection } = useTranslation();
 *
 * // Simple usage (keys are relative to translations passed to provider)
 * <h1>{t('page_title')}</h1>
 *
 * // With fallback
 * <button>{t('actions.save', 'Save')}</button>
 *
 * // Get a section for props
 * const formLabels = getSection('form.labels');
 */
export function useTranslation() {
  const context = useContext(TranslationContext);

  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }

  return context;
}

// Export types for consumers
export type { TranslationContextValue, TranslationProviderProps };
